"use client";

import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, MessageSquare, Search, UserPlus } from 'lucide-react';
import { format } from 'date-fns';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {

  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useUserMessagesContext } from '@/contexts/UserMessagesContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface UserConnectionsProps {
  onStartMessaging: (userId: string, userName: string) => void;
}

const getInitials = (value?: string | null) => {
  if (!value) {
    return 'U';
  }

  return value
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const getOtherUserId = (connectionUserId: string, connectionTargetId: string, currentUserId?: string) =>
  connectionUserId === currentUserId ? connectionTargetId : connectionUserId;

export default function UserConnections({ onStartMessaging }: UserConnectionsProps) {
  const { connections, requestConnection, onlineUsers } = useUserMessagesContext();
  const { user } = useAuth();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ownMessengerId, setOwnMessengerId] = useState<string | null>(null);
  const [ownMessengerIdLoading, setOwnMessengerIdLoading] = useState(true);
  const [lookupInput, setLookupInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [lookupResult, setLookupResult] = useState<{
    userId: string;
    messengerId: string;
    displayName: string;
    avatarUrl?: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadOwnMessengerId = async () => {
      if (!user) {
        setOwnMessengerId(null);
        setOwnMessengerIdLoading(false);
        return;
      }

      setOwnMessengerIdLoading(true);
      const { data, error } = await supabase.rpc('ensure_messenger_identity');

      if (cancelled) {
        return;
      }

      if (error) {
        console.error('[UserConnections] Failed to load Messenger ID:', error);
        setOwnMessengerId(null);
      } else {
        setOwnMessengerId(data ? String(data) : null);
      }

      setOwnMessengerIdLoading(false);
    };

    void loadOwnMessengerId();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (dialogOpen) {
      return;
    }

    setLookupInput('');
    setLookupResult(null);
    setLookupLoading(false);
    setAdding(false);
  }, [dialogOpen]);

  const acceptedConnections = useMemo(
    () =>
      connections.filter(
        (connection) =>
          connection.status === 'accepted' &&
          (connection.user_id === user?.id || connection.connected_user_id === user?.id)
      ),
    [connections, user]
  );

  const matchedConnection = useMemo(() => {
    if (!lookupResult || !user) {
      return null;
    }

    return (
      connections.find(
        (connection) =>
          (connection.user_id === user.id && connection.connected_user_id === lookupResult.userId) ||
          (connection.connected_user_id === user.id && connection.user_id === lookupResult.userId)
      ) ?? null
    );
  }, [connections, lookupResult, user]);

  const lookupState = !matchedConnection
    ? 'none'
    : matchedConnection.status === 'accepted'
      ? 'accepted'
      : matchedConnection.connected_user_id === user?.id
        ? 'pending-incoming'
        : 'pending-outgoing';

  const handleCopyOwnId = async () => {
    if (!ownMessengerId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(ownMessengerId);
      toast({
        title: 'Messenger ID copied',
        description: 'Share it directly with someone you want to add.',
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Please copy the ID manually.',
        variant: 'destructive',
      });
    }
  };

  const handleLookup = async () => {
    const normalized = lookupInput.replace(/\D/g, '').slice(0, 12);
    if (!normalized) {
      toast({
        title: 'Enter a Messenger ID',
        description: 'Ask the other person for their numeric Messenger ID.',
        variant: 'destructive',
      });
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);

    try {
      const { data, error } = await supabase.rpc('lookup_messenger_user', {
        p_messenger_id: Number.parseInt(normalized, 10),
      });

      if (error) {
        console.error('[UserConnections] Failed to look up Messenger user:', error);
        toast({
          title: 'Lookup failed',
          description: 'Could not look up that Messenger ID right now.',
          variant: 'destructive',
        });
        return;
      }

      const match = data?.[0];
      if (!match) {
        toast({
          title: 'No user found',
          description: 'Check the Messenger ID and try again.',
          variant: 'destructive',
        });
        return;
      }

      setLookupResult({
        userId: match.user_id,
        messengerId: String(match.messenger_id),
        displayName: match.display_name || 'User',
        avatarUrl: match.avatar_url,
      });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAddConnection = async () => {
    if (!lookupResult) {
      return;
    }

    setAdding(true);
    try {
      const result = await requestConnection(lookupResult.userId);
      if (result) {
        toast({
          title: 'Connection request sent',
          description: `A connection request has been sent to ${lookupResult.displayName}.`,
        });
        setDialogOpen(false);
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-lg font-semibold">My Connections</h2>
          <p className="text-sm text-muted-foreground">Add people by shared Messenger ID instead of public profile search.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Your ID</p>
              <p className="font-mono text-sm font-semibold tracking-[0.16em] text-foreground">
                {ownMessengerIdLoading ? 'Loading…' : ownMessengerId ?? 'Unavailable'}
              </p>
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0"
              onClick={handleCopyOwnId}
              disabled={!ownMessengerId || ownMessengerIdLoading}
              aria-label="Copy your Messenger ID"
              title="Copy your Messenger ID"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => setDialogOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Add by ID
          </Button>
        </div>
      </div>

      {acceptedConnections.length === 0 ? (
        <div className="text-muted-foreground">No connections yet.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {acceptedConnections.map((connection) => {
            const otherUserId = getOtherUserId(connection.user_id, connection.connected_user_id, user?.id);
            const otherUserName = connection.connected_user_name || 'User';
            const otherUserAvatar = connection.connected_user_avatar || undefined;
            const isOnline = onlineUsers.has(otherUserId);

            return (
              <Card key={connection.id}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar>
                        <AvatarImage src={otherUserAvatar} alt={otherUserName} />
                        <AvatarFallback>{getInitials(otherUserName)}</AvatarFallback>
                      </Avatar>
                      <span
                        className={cn(
                          'absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full border-2 border-card',
                          isOnline ? 'bg-green-500' : 'bg-gray-400'
                        )}
                        title={isOnline ? 'Online' : 'Offline'}
                      />
                    </div>
                    <CardTitle className="text-md">{otherUserName}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    Connected on {format(new Date(connection.updated_at), 'PP')}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button size="sm" variant="outline" onClick={() => onStartMessaging(otherUserId, otherUserName)}>
                    <MessageSquare size={14} className="mr-1" />
                    Message
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Connection</DialogTitle>
            <DialogDescription>
              Enter the exact numeric Messenger ID someone shared with you.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={lookupInput}
                onChange={(event) => setLookupInput(event.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="Enter Messenger ID"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleLookup();
                  }
                }}
              />
              <Button type="button" variant="outline" className="gap-2" onClick={handleLookup} disabled={lookupLoading}>
                {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Lookup
              </Button>
            </div>

            {lookupResult && (
              <div className="rounded-xl border border-border/70 bg-card/70 p-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-11 w-11 border border-border/50">
                    <AvatarImage src={lookupResult.avatarUrl || undefined} alt={lookupResult.displayName} />
                    <AvatarFallback>{getInitials(lookupResult.displayName)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-foreground">{lookupResult.displayName}</p>
                      <Badge variant="outline" className="font-mono text-[10px] tracking-[0.16em]">
                        {lookupResult.messengerId}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {lookupState === 'accepted'
                        ? 'You are already connected.'
                        : lookupState === 'pending-incoming'
                          ? 'This user already sent you a request.'
                          : lookupState === 'pending-outgoing'
                            ? 'Your request is already pending.'
                            : 'This user can be added to your connections.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              onClick={handleAddConnection}
              disabled={!lookupResult || lookupState !== 'none' || adding}
            >
              {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Add Connection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
